
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Container, Box, Typography, Button, Divider,
    Chip, CircularProgress,
} from '@mui/material';
import {
    LocationOn as LocationIcon,
    Receipt as ReceiptIcon,
    CreditCard as CardIcon,
    Lock as LockIcon,
    Shield as ShieldIcon,
    ArrowBack as BackIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import billService from '../services/billService';
import { useCart } from '../context/CartContext';
import '../styles/ProtectedRoutes.css';

let razorpayLoadPromise = null;
const loadRazorpay = () => {
    if (window.Razorpay) return Promise.resolve(true);
    if (razorpayLoadPromise) return razorpayLoadPromise; // avoid injecting the script twice on rapid retries

    razorpayLoadPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => {
            razorpayLoadPromise = null; // allow retry on next attempt
            resolve(false);
        };
        document.body.appendChild(script);
    });
    return razorpayLoadPromise;
};

/* ── Design tokens ───────────────────────────────────────────────────── */
const GOLD = '#bc965e';
const GOLD_LIGHT = 'rgba(188,150,94,0.10)';
const GOLD_BORDER = 'rgba(188,150,94,0.35)';
const GOLD_BORDER_STRONG = 'rgba(188,150,94,0.60)';
const SURFACE = 'rgba(255,255,255,0.03)';
const SURFACE_HOVER = 'rgba(188,150,94,0.06)';
const BORDER = 'rgba(188,150,94,0.18)';
const TEXT_PRIMARY = '#ffffff';
const TEXT_SECONDARY = '#ffffff';
const TEXT_MUTED = '#ffffff';
const GREEN = '#4caf50';
const GREEN_BG = 'rgba(76,175,80,0.10)';
const GREEN_BORDER = 'rgba(76,175,80,0.30)';

/* ── Shared sx helpers ───────────────────────────────────────────────── */
const card = {
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 3,
    p: { xs: 2, sm: 2.5 },
};

const sectionLabel = {
    display: 'flex', alignItems: 'center', gap: 1,
    color: TEXT_MUTED, fontWeight: 600,
    fontSize: '0.7rem', letterSpacing: '0.08em',
    textTransform: 'uppercase', mb: 1.75,
};

const PAYMENT_CHIPS = ['UPI', 'Visa', 'Mastercard', 'Netbanking'];

/* ── Breadcrumb step ─────────────────────────────────────────────────── */
const Step = ({ label, active, done }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Box sx={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: done ? GREEN_BG : active ? GOLD_LIGHT : 'rgba(255,255,255,0.05)',
            border: `1px solid ${done ? GREEN_BORDER : active ? GOLD_BORDER : 'rgba(255,255,255,0.12)'}`,
        }}>
            {done
                ? <CheckIcon sx={{ fontSize: 11, color: GREEN }} />
                : <Typography sx={{ fontSize: '0.6rem', color: active ? GOLD : TEXT_MUTED, fontWeight: 600 }}>
                    {label.charAt(0)}
                </Typography>
            }
        </Box>
        <Typography sx={{
            fontSize: '0.72rem', fontWeight: active ? 600 : 400,
            color: active ? GOLD : done ? TEXT_SECONDARY : TEXT_MUTED,
        }}>
            {label}
        </Typography>
    </Box>
);

/* ── Summary row ─────────────────────────────────────────────────────── */
const SummaryRow = ({ label, value, labelSx = {}, valueSx = {} }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.82rem', color: TEXT_SECONDARY, ...labelSx }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', color: TEXT_PRIMARY, ...valueSx }}>{value}</Typography>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════════ */
const PaymentPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshCart } = useCart();

    const { address, appliedCoupon, cartData, cartTotal, shippingCharge = 0, finalTotal } = location.state || {};

    const [paying, setPaying] = useState(false);
    const payingRef = useRef(false); // guards against double-submit from rapid double-clicks before state flushes

    // Parsed once — sessionStorage doesn't change during this page's lifetime
    const referrerToken = useMemo(() => {
        const referralData = sessionStorage.getItem('referralToken');
        if (!referralData) return null;
        try {
            return JSON.parse(referralData).token ?? null;
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        if (!address || !cartData) {
            toast.error('Session expired. Please start again.');
            navigate('/cart');
        }
        // Intentionally run only on mount: address/cartData come from router state
        // and won't change without a full navigation to this page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnlinePayment = useCallback(async () => {
        if (payingRef.current) return; // hard guard, independent of React state timing
        payingRef.current = true;
        setPaying(true);

        try {
            const loaded = await loadRazorpay();
            if (!loaded) {
                toast.error('Failed to load payment gateway. Check your internet.');
                return;
            }

            const { data: paymentData } = await API.post('/payments/create-order', { amount: finalTotal });
            if (!paymentData.success) throw new Error(paymentData.message);

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: paymentData.order.amount,
                currency: 'INR',
                name: 'Ginni Fashion Studio',
                description: 'Order Payment',
                order_id: paymentData.order.id,
                prefill: { name: address.FullName, contact: address.PhoneNumber },
                theme: { color: GOLD },

                handler: async (response) => {
                    try {
                        const { data: verifyData } = await API.post('/payments/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        if (!verifyData.success) {
                            toast.error('Payment verification failed. Contact support.');
                            return;
                        }
                        const data = await billService.checkout({
                            addressId: address.AddressId,
                            couponCode: appliedCoupon?.CouponCode || null,
                            payment_method: 'online',
                            razorpay_payment_id: response.razorpay_payment_id,
                            shippingCharge,
                            referrerToken,
                        });
                        if (data.success) {
                            toast.success('Order placed successfully! 🎉');
                            await refreshCart();
                            navigate('/orders', { state: { billNumber: data.billNumber } });
                            return; // navigating away — skip the finally-driven reset below
                        }
                    } catch (err) {
                        toast.error(err.response?.data?.message || 'Order placement failed.');
                    } finally {
                        payingRef.current = false;
                        setPaying(false);
                    }
                },

                modal: {
                    ondismiss: () => {
                        toast.info('Payment cancelled.');
                        payingRef.current = false;
                        setPaying(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (r) => {
                toast.error(`Payment failed: ${r.error.description}`);
                payingRef.current = false;
                setPaying(false);
            });
            rzp.open();
            // Note: we deliberately don't reset `paying` here — the checkout
            // modal is open and the flow now continues via handler/ondismiss/payment.failed above.
        } catch (err) {
            toast.error(err.message || 'Something went wrong.');
            payingRef.current = false;
            setPaying(false);
        }
    }, [address, appliedCoupon, finalTotal, navigate, referrerToken, refreshCart, shippingCharge]);

    if (!address || !cartData) return null;

    const couponDiscount = appliedCoupon ? parseFloat(appliedCoupon.DiscountAmount || 0) : 0;

    /* ── Render ──────────────────────────────────────────────────────────── */
    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>

            {/* ── Header ───────────────────────────────────────────────── */}
            <Box sx={{ mb: { xs: 2.5, sm: 3.5 } }} className="glass-content-container">

                {/* Back button */}
                <Box sx={{ mb: 1, textAlign: 'left' }}>
                    <Button
                        onClick={() => navigate(-1)}
                        disabled={paying}
                        startIcon={<BackIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{
                            textTransform: 'none',
                            color: TEXT_SECONDARY,
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            px: 1,
                            minWidth: 0,
                            display: 'inline-flex',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: GOLD },
                        }}
                    >
                        Back
                    </Button>
                </Box>

                <Typography
                    variant="h4"
                    className="glass-title"
                    sx={{ fontSize: { xs: '1.35rem', sm: '1.75rem' }, fontWeight: 700, mb: 0.5 }}
                >
                    Review &amp; Pay
                </Typography>

                <Typography variant="body2" sx={{ color: TEXT_MUTED, fontSize: '0.82rem', mb: 2 }}>
                    Confirm your order before payment
                </Typography>

                {/* Breadcrumb */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 }, flexWrap: 'wrap' }}>
                    <Step label="Cart" done />
                    <Typography sx={{ color: TEXT_MUTED, fontSize: '0.65rem', mx: 0.25 }}>──</Typography>
                    <Step label="Address" done />
                    <Typography sx={{ color: TEXT_MUTED, fontSize: '0.65rem', mx: 0.25 }}>──</Typography>
                    <Step label="Review & Pay" active />
                </Box>
            </Box>

            {/* ── Two-column layout ─────────────────────────────────────── */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr 320px' },
                gap: { xs: 2, sm: 2.5 },
                alignItems: 'start',
            }}>

                {/* ── LEFT ─────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Delivery Address */}
                    <Box sx={card}>
                        <Typography sx={sectionLabel}>
                            <LocationIcon sx={{ fontSize: 14 }} /> Delivery address
                        </Typography>

                        <Box sx={{
                            p: { xs: 1.5, sm: 2 }, borderRadius: 1,
                            background: GOLD_LIGHT,
                            border: `1px solid ${GOLD_BORDER}`,
                        }}>
                            {/* Name + badge row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6, flexWrap: 'wrap' }}>
                                <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: '0.9rem' }}>
                                    {address.FullName}
                                </Typography>
                                {address.IsDefault && (
                                    <Chip
                                        label="Default"
                                        size="small"
                                        sx={{
                                            height: 18, fontSize: '0.6rem', fontWeight: 700,
                                            background: GREEN_BG,
                                            border: `1px solid ${GREEN_BORDER}`,
                                            color: GREEN,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Address text */}
                            <Typography variant="body2" sx={{ color: TEXT_SECONDARY, lineHeight: 1.65, fontSize: '0.82rem', mb: 0.6 }}>
                                {address.Address}, {address.City}, {address.State} – {address.Pincode}, India
                            </Typography>

                            {/* Phone */}
                            <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block', mb: 1.25 }}>
                                📞 {address.PhoneNumber}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Payment Method */}
                    <Box sx={card}>
                        <Typography sx={sectionLabel}>
                            <CardIcon sx={{ fontSize: 14 }} /> Payment method
                        </Typography>

                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            p: { xs: 1.5, sm: 1.75 }, borderRadius: 1,
                            background: SURFACE_HOVER,
                            border: `1px solid ${GOLD_BORDER_STRONG}`,
                        }}>
                            <Box sx={{
                                width: 38, height: 38, borderRadius: 2, flexShrink: 0,
                                background: GOLD_LIGHT,
                                border: `1px solid ${GOLD_BORDER}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <CardIcon sx={{ color: GOLD, fontSize: 18 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: '0.875rem' }}>
                                    Online Payment
                                </Typography>
                                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.75rem', mt: 0.2 }}>
                                    Cards · UPI · Netbanking · Wallets via Razorpay
                                </Typography>
                            </Box>
                            <Chip
                                label="Secured"
                                size="small"
                                sx={{
                                    height: 20, fontSize: '0.62rem', fontWeight: 700, flexShrink: 0,
                                    background: GREEN_BG,
                                    border: `1px solid ${GREEN_BORDER}`,
                                    color: GREEN,
                                }}
                            />
                        </Box>

                        {/* Trust line */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            mt: 2, pt: 1.5,
                            borderTop: `1px solid ${BORDER}`,
                        }}>
                            <ShieldIcon sx={{ fontSize: 13, color: TEXT_MUTED }} />
                            <Typography sx={{ fontSize: '0.72rem', color: TEXT_MUTED }}>
                                256-bit SSL encryption · PCI-DSS compliant checkout
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* ── RIGHT — Order Summary ────────────────────────────── */}
                <Box sx={{
                    position: { lg: 'sticky' },
                    top: { lg: 80 },
                    border:"1px solid rgba(188, 150, 94, 0.51)",
                    borderRadius:1
                }}>
                    <Box sx={card}>
                        <Typography sx={sectionLabel}>
                            <ReceiptIcon sx={{ fontSize: 14 }} /> Order summary
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                            {/* Item count */}
                            <SummaryRow
                                label={`Subtotal (${cartData.items.length} item${cartData.items.length !== 1 ? 's' : ''})`}
                                value={`₹${cartTotal.toLocaleString()}`}
                            />

                            {/* Shipping */}
                            <SummaryRow
                                label="Shipping"
                                value={shippingCharge === 0 ? 'Free' : `₹${parseFloat(shippingCharge).toFixed(2)}`}
                                valueSx={{ color: shippingCharge === 0 ? GREEN : GOLD, fontWeight: 500 }}
                            />

                            {/* Coupon */}
                            {appliedCoupon && (
                                <SummaryRow
                                    label={`Coupon (${appliedCoupon.CouponCode})`}
                                    value={`−₹${couponDiscount.toLocaleString()}`}
                                    labelSx={{ color: GREEN }}
                                    valueSx={{ color: GREEN, fontWeight: 600 }}
                                />
                            )}

                            <Divider sx={{ borderColor: BORDER, my: 0.25 }} />

                            {/* Total */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: '0.95rem' }}>Total</Typography>
                                <Typography sx={{
                                    color: appliedCoupon ? GREEN : GOLD,
                                    fontWeight: 700, fontSize: '1.25rem',
                                }}>
                                    ₹{finalTotal.toLocaleString()}
                                </Typography>
                            </Box>

                            {/* Savings badge */}
                            {appliedCoupon && (
                                <Box sx={{
                                    p: 1, borderRadius: 1.5, textAlign: 'center',
                                    background: GREEN_BG,
                                    border: `1px dashed ${GREEN_BORDER}`,
                                }}>
                                    <Typography variant="caption" sx={{ color: GREEN, fontSize: '0.72rem' }}>
                                        🎉 You save ₹{couponDiscount.toLocaleString()} with {appliedCoupon.CouponCode}
                                    </Typography>
                                </Box>
                            )}

                            {/* Pay button */}
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={handleOnlinePayment}
                                disabled={paying}
                                startIcon={
                                    paying
                                        ? <CircularProgress size={16} color="inherit" />
                                        : <LockIcon sx={{ fontSize: '17px !important' }} />
                                }
                                sx={{
                                    mt: 0.5,
                                    background: `${GOLD} !important`,
                                    color: '#000',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                                    py: 1.4,
                                    borderRadius: 1,
                                    letterSpacing: '0.01em',
                                    boxShadow: 'none !important',
                                    '&:hover': { background: `#a07848 !important` },
                                    '&.Mui-disabled': {
                                        background: 'rgba(188,150,94,0.25) !important',
                                        color: 'rgba(0,0,0,0.35)',
                                    },
                                }}
                            >
                                {paying ? 'Processing…' : `Pay ₹${finalTotal.toLocaleString()} securely`}
                            </Button>

                            <Divider sx={{ borderColor: BORDER }} />

                            {/* Accepted payment chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, justifyContent: 'center' }}>
                                {PAYMENT_CHIPS.map((m) => (
                                    <Chip
                                        key={m}
                                        label={m}
                                        size="small"
                                        sx={{
                                            height: 19, fontSize: '0.6rem', fontWeight: 600,
                                            background: GOLD_LIGHT,
                                            border: `1px solid ${GOLD_BORDER}`,
                                            color: GOLD,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Container>
    );
};

export default PaymentPage;